import * as Sentry from '@sentry/browser';
import { removeThumbsupVideo, thumbsupVideo } from 'Api/videos';
import 'Components/vm-button/vm-button';
import 'Components/vm-icon/vm-icon';
import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ApiFetchError } from 'Utils/api-fetch-request';
import Styles from './vm-video-thumbs-up-button.scss';

/**
 * Thumbs-up toggle for a video. Owns the optimistic count/state toggle and
 * the POST/DELETE call. Reverts and shows an error toast on failure.
 *
 * Rapid re-clicks while a request is in flight are silently ignored to
 * prevent the local state from drifting from server state.
 */
@customElement('vm-video-thumbs-up-button')
export class VmVideoThumbsUpButton extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'video-slug' })
    videoSlug = '';

    @property({ type: Number, attribute: 'thumbs-up-count' })
    thumbsUpCount = 0;

    @property({ type: Boolean, attribute: 'user-has-liked' })
    userHasLiked = false;

    @state()
    private _pending = false;

    private _onClick = async () => {
        if (this._pending || !this.videoSlug) return;

        const wasLiked = this.userHasLiked;
        const previousCount = this.thumbsUpCount;

        this.userHasLiked = !wasLiked;
        this.thumbsUpCount = wasLiked ? previousCount - 1 : previousCount + 1;
        this._pending = true;

        try {
            if (wasLiked) {
                await removeThumbsupVideo(this.videoSlug);
            } else {
                await thumbsupVideo(this.videoSlug);
            }
        } catch (e) {
            this.userHasLiked = wasLiked;
            this.thumbsUpCount = previousCount;

            if (!(e instanceof ApiFetchError)) {
                Sentry.captureException(e, {
                    extra: {
                        videoSlug: this.videoSlug,
                        action: wasLiked ? 'unlike' : 'like',
                    },
                });
            }

            document.dispatchEvent(
                new CustomEvent('vm-toast-show', {
                    detail: {
                        message: wasLiked ? 'Could not remove thumbs up.' : 'Could not add thumbs up.',
                        variant: 'error',
                    },
                }),
            );
        } finally {
            this._pending = false;
        }
    };

    override render() {
        const thumbsUpIcon = `thumbs-up${this.userHasLiked ? '-solid' : ''}` as const;
        return html`
            <vm-button variant="solid" color-scheme="secondary" size="sm" @click=${this._onClick}>
                <vm-icon slot="icon-leading" name=${thumbsUpIcon} size="sm"></vm-icon>
                ${this.thumbsUpCount > 0 ? `${this.thumbsUpCount} ` : ''}Thumbs up
            </vm-button>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-thumbs-up-button': VmVideoThumbsUpButton;
    }
}
